import * as Yup from "yup";
import moment from "moment";
import { Formik } from "formik";
import pluralize from "pluralize";
import Datetime from "react-datetime";
import { useDispatch } from "react-redux";
import { toastr } from "react-redux-toastr";
import { useParams } from "react-router-dom";
import React, { useEffect, useState } from "react";
import { Accordion, Col, Row } from "react-bootstrap";
import Typography from "../../../../common/Typography";
import Button from "../../../../common/Buttons/Button";
import Input from "../../../../common/TextFields/Input";
import SearchBar from "../../../../common/Dropdowns/Select/SearchBar";
import ProfileCompleteAlert from "../../../../common/Modals/AlertModals/ProfileCompleteAlert";
import {
  updateCustDetails,
  setSortlinkDetails,
} from "../../../../../redux/actions/shortlinkActions";
import {
  getPlacesFromOla,
  getPlacesFromGoogle,
} from "../../../../../redux/actions/utilityActions";

const CompleteProfileCard = ({
  loyaltyData,
  customerData,
  setIsLoading,
  currencyView,
  isCashbackLoyalty,
}) => {
  const params = useParams();
  const dispatch = useDispatch();
  const [isProfileCompletedModal, setProfileCompletedModal] = useState(false);
  const [modalText, setModalText] = useState(
    "You're all good! You've succesfully completed your profile."
  );
  const [modalImgSrc, setModalImgSrc] = useState(
    "/assets/images/shortlinks/profileComplete2.svg"
  );
  const [modalNextText, setModalNextText] = useState("Okay");
  const [isSearching, toggleSearch] = useState(false);

  const [searchedQuery, setSearchedQuery] = useState("");
  const [delayedSearchedQuery, setDelayedSearchedQuery] = useState("");

  const [selectedPlace, setSelectedPlace] = useState("");
  const [searchedResultsArr, setSearchedResultsArr] = useState([]);
  const isIndianTimezone = new Date().getTimezoneOffset() === -330;

  useEffect(() => {
    if (customerData?.address?.length > 0)
      setSelectedPlace({
        value: customerData?.address,
        label: customerData?.address,
        location: customerData?.location,
      });
  }, [customerData]);

  useEffect(() => {
    toggleSearch(true);
    const delayDebounceFn = setTimeout(() => {
      setDelayedSearchedQuery(searchedQuery);
    }, 1000);

    return () => clearTimeout(delayDebounceFn);
  }, [searchedQuery]);

  useEffect(() => {
    if (delayedSearchedQuery?.length > 0) handleSearch(delayedSearchedQuery);
  }, [delayedSearchedQuery]);

  const handleSearch = (query) => {
    if (isIndianTimezone) {
      getPlacesFromOla(query)
        .then((payload) => {
          toggleSearch(false);
          const searchedOptions = payload?.map((obj) => ({
            label: `${obj?.description}`,
            value: `${obj?.description}`,
            title: obj?.structured_formatting?.main_text,
            subtitle: obj?.structured_formatting?.secondary_text,
            location: {
              latitude: obj?.geometry?.location?.lat,
              longitude: obj?.geometry?.location?.lng,
            },
          }));
          setSearchedResultsArr(searchedOptions);
        })
        .catch(() => toggleSearch(false));
    } else {
      const reqBody = {
        textQuery: query,
      };
      getPlacesFromGoogle(
        reqBody,
        "places.formattedAddress,places.location,places.displayName.text"
      )
        .then((payload) => {
          toggleSearch(false);
          const searchedOptions = payload?.map((obj) => ({
            ...obj,
            title: obj?.displayName?.text,
            subtitle: obj?.formattedAddress,
            label: `${obj?.displayName?.text}, ${obj?.formattedAddress}`,
            value: `${obj?.displayName?.text}, ${obj?.formattedAddress}`,
          }));
          setSearchedResultsArr(searchedOptions);
        })
        .catch(() => toggleSearch(false));
    }
  };

  const handleMenuClose = () => {
    setSearchedResultsArr([]);
  };

  const handleSelectPlace = (obj) => {
    setSelectedPlace(obj);
  };

  return (
    <Accordion className="shortlink-form" flush defaultActiveKey={2}>
      <Accordion.Item>
        <Accordion.Header>
          <div className="head">
            <img
              alt="badge"
              width="40px"
              height="40px"
              className="high-voltage"
              src="/assets/icons/highVoltage.svg"
            />
            <Typography tag="h4" weight="medium" size="text-md">
              Share your details and get{" "}
              {loyaltyData?.is_active &&
              loyaltyData?.settings?.profile_reward?.status ? (
                isCashbackLoyalty ? (
                  <>
                    <Typography tag="span" weight="semibold" size="text-md">
                      {currencyView}
                      {loyaltyData?.settings?.profile_reward?.points}
                    </Typography>{" "}
                    to spend on next purchase
                  </>
                ) : (
                  <>
                    <Typography tag="span" weight="semibold" size="text-md">
                      {loyaltyData?.settings?.profile_reward?.points} bonus{" "}
                      {pluralize(
                        loyaltyData?.settings?.points_branding?.active
                          ? loyaltyData?.settings?.points_branding?.name
                          : "point",
                        loyaltyData?.settings?.profile_reward?.points
                      )}
                    </Typography>{" "}
                    on next purchase
                  </>
                )
              ) : (
                <>
                  rewarded on your{" "}
                  <Typography tag="span" weight="semibold" size="text-md">
                    special days!
                  </Typography>
                </>
              )}
            </Typography>
          </div>
        </Accordion.Header>
        <Accordion.Body>
          <Formik
            initialValues={{
              name: customerData?.name || "",
              email: customerData?.email || "",
              gender: customerData?.gender || "male",
              birthday:
                (customerData?.birthday && moment(customerData.birthday)) || "",
              anniversary:
                (customerData?.anniversary &&
                  moment(customerData.anniversary)) ||
                "",
            }}
            validationSchema={validationSchema}
            onSubmit={(values, { setSubmitting, setErrors }) => {
              const locationAddress = {
                address: selectedPlace?.label,
                location: selectedPlace?.location,
              };
              let duplicateValues = { ...values, ...locationAddress };
              if (
                duplicateValues.birthday &&
                !moment.isMoment(duplicateValues.birthday)
              ) {
                const momentDate = moment(
                  duplicateValues.birthday,
                  "ddd MMM DD YYYY HH:mm:ss [GMT]ZZ"
                );
                if (momentDate.isValid()) {
                  duplicateValues.birthday = momentDate.format("yyyy-MM-DD");
                } else {
                  setSubmitting(false);
                  toastr.error("Validation Error", "Not a valid birthday date");
                  return;
                }
              } else if (moment.isMoment(duplicateValues.birthday)) {
                duplicateValues.birthday =
                  duplicateValues.birthday.format("yyyy-MM-DD");
              }

              if (
                duplicateValues.anniversary &&
                duplicateValues.anniversary?.length > 0 &&
                !moment.isMoment(duplicateValues.anniversary)
              ) {
                const momentDate = moment(
                  duplicateValues.anniversary,
                  "ddd MMM DD YYYY HH:mm:ss [GMT]ZZ"
                );
                if (momentDate.isValid()) {
                  duplicateValues.anniversary = momentDate.format("yyyy-MM-DD");
                } else {
                  setSubmitting(false);
                  toastr.error(
                    "Validation Error",
                    "Not a valid anniversary date"
                  );
                  return;
                }
              } else if (moment.isMoment(duplicateValues.anniversary))
                duplicateValues.anniversary =
                  duplicateValues.anniversary.format("yyyy-MM-DD");

              if (duplicateValues?.anniversary?.length === 0)
                delete duplicateValues?.anniversary;

              dispatch(updateCustDetails(params?.code, duplicateValues))
                .then(() => {
                  setSubmitting(false);
                  setIsLoading(true);
                  dispatch(setSortlinkDetails(params.code)).then((payload) => {
                    setIsLoading(false);
                    if (payload?.profile_complete) {
                      if (
                        payload?.loyalty?.settings?.profile_reward?.status &&
                        loyaltyData?.is_active
                      ) {
                        setModalText(
                          `Congrats! You earned ${
                            payload?.loyalty?.settings?.profile_reward?.points
                          } bonus ${isCashbackLoyalty ? "cash" : "pts"} for ${
                            payload?.loyalty?.title
                          } for completing your profile! Your ${
                            isCashbackLoyalty ? "cash" : "pts"
                          } bal is ${payload?.total_points}`
                        );
                        setModalImgSrc(
                          "/assets/images/shortlinks/profileComplete1.svg"
                        );
                        setModalNextText("Awesome!");
                      }
                      setProfileCompletedModal(true);
                    }
                    // trackEvent("ProfileDetailsAdded", {
                    //   source: "loyalty_shortlink",
                    //   address_entered:
                    //     duplicateValues?.address &&
                    //     duplicateValues?.address?.length > 0,
                    //   anniversary_entered:
                    //     duplicateValues?.anniversary &&
                    //     duplicateValues?.anniversary?.length > 0,
                    //   is_profile_complete: payload?.profile_complete,
                    // });
                  });
                })
                .catch((errors) => {
                  setSubmitting(false);
                  if (Array.isArray(errors)) {
                    var object = errors?.reduce((obj, item) => {
                      obj[item.param] = item.msg;
                      return obj;
                    }, {});
                    setErrors(object);
                  }
                });
            }}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleSubmit,
              isSubmitting,
              setFieldValue,
            }) => {
              return (
                <>
                  <Input
                    disabled={true}
                    label="Phone Number"
                    value={customerData?.phone}
                  />
                  <Input
                    name="name"
                    label="Name"
                    error={errors.name}
                    value={values.name}
                    touch={touched.name}
                    placeholder="Enter name"
                    handleChange={handleChange}
                  />
                  <div className="cust-steps">
                    <div
                      className={`step${
                        values.gender === "male" ? " active-step" : ""
                      }`}
                      onClick={() => setFieldValue("gender", "male")}
                    >
                      <Typography size="text-sm" weight="medium">
                        Male
                      </Typography>
                    </div>
                    <div
                      className={`step${
                        values.gender === "female" ? " active-step" : ""
                      }`}
                      onClick={() => setFieldValue("gender", "female")}
                    >
                      <Typography size="text-sm" weight="medium">
                        Female
                      </Typography>
                    </div>
                  </div>
                  {errors.gender && (
                    <div className="field-error">{errors.gender}</div>
                  )}
                  <Input
                    type="email"
                    name="email"
                    label="Email"
                    error={errors.email}
                    value={values.email}
                    touch={touched.email}
                    placeholder="Enter email"
                    handleChange={handleChange}
                  />
                  <Row className="genrow">
                    <Col xs={6} className="gencol">
                      <label className="field-label">Birthday</label>
                      <Datetime
                        timeFormat={false}
                        dateFormat="DD-MM-YYYY"
                        value={values.birthday}
                        onChange={(e) => setFieldValue("birthday", e)}
                        isValidDate={(current) => current.isBefore(moment())}
                        className={`new-date-field${
                          errors.birthday ? " add-margin" : ""
                        }`}
                        inputProps={{
                          name: "birthday",
                          autoComplete: "off",
                          placeholder: "dd-mm-yyyy",
                          disabled: customerData?.birthday ? true : false,
                        }}
                      />
                      <div className="field-error">{errors.birthday}</div>
                    </Col>
                    <Col xs={6} className="gencol">
                      <label className="field-label">Anniversary</label>
                      <Datetime
                        timeFormat={false}
                        dateFormat="DD-MM-YYYY"
                        value={values.anniversary}
                        onChange={(e) => setFieldValue("anniversary", e)}
                        isValidDate={(current) => current.isBefore(moment())}
                        className={`new-date-field${
                          errors.anniversary ? " add-margin" : ""
                        }`}
                        inputProps={{
                          name: "anniversary",
                          autoComplete: "off",
                          placeholder: "dd-mm-yyyy",
                          disabled: customerData?.anniversary ? true : false,
                        }}
                      />
                      <div className="field-error">{errors.anniversary}</div>
                    </Col>
                  </Row>
                  <SearchBar
                    label="Address"
                    fullWidth={true}
                    value={selectedPlace}
                    hasMoreOptions={false}
                    isSearching={isSearching}
                    inputValue={searchedQuery}
                    onMenuClose={handleMenuClose}
                    optionsList={searchedResultsArr}
                    handleSelect={handleSelectPlace}
                    placeholder="Search your address"
                    menuCustomClassName="google-places-search-menu"
                    onInputChange={(inputVal) => setSearchedQuery(inputVal)}
                  />
                  <Button
                    type="submit"
                    text="Submit"
                    fullWidth={true}
                    disabled={isSubmitting}
                    handleClick={handleSubmit}
                    isSubmitting={isSubmitting}
                  />
                </>
              );
            }}
          </Formik>
        </Accordion.Body>
      </Accordion.Item>

      <ProfileCompleteAlert
        text={modalText}
        imgSrc={modalImgSrc}
        nextText={modalNextText}
        isOpen={isProfileCompletedModal}
        toggleModal={() => setProfileCompletedModal(!isProfileCompletedModal)}
      />
    </Accordion>
  );
};

export default CompleteProfileCard;

const validationSchema = () =>
  Yup.object().shape({
    email: Yup.string()
      .email("Invalid email")
      .required("Please enter Your email"),
    name: Yup.string().required("Please enter your name"),
    gender: Yup.string().required("Please select your gender"),
    birthday: Yup.string().required("Please select your birthday"),
  });
